// Weekly training load aggregation with ACWR-derived optimal band.
//
// For each week, optimalLow / optimalHigh are derived from the rolling average
// of the prior 4 weeks (training-history weeks only — zero-TSS weeks are ignored
// to avoid the band collapsing after a rest/off-season block). The band uses
// the same 0.8 / 1.3 bounds as the ACWR gauge for internal consistency.

// Return YYYY-MM-DD in local time — avoids UTC drift when workouts straddle
// midnight in the athlete's timezone. Mirrors trainingSummary.js / proactiveAlerts.js.
function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Monday-start week: shift back by (day - 1) days, or 6 days for Sunday.
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function shortLabel(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Compute weekly TSS totals and ACWR-derived optimal bands.
 *
 * @param   {Array}  workouts        Normalised workout array (TSS should be pre-enriched)
 * @param   {Object} [options]
 * @param   {number} [options.weeks=8]  Number of weeks to return, current week last
 * @param   {Date}   [options.today]    Override "now" — primarily for tests
 * @returns {Array<{ weekStart: string, weekLabel: string, totalTSS: number,
 *                   optimalLow: number|null, optimalHigh: number|null,
 *                   status: 'under'|'optimal'|'over'|'unknown',
 *                   workoutCount: number, isEstimated: boolean, isCurrentWeek: boolean }>}
 */
export function computeWeeklyLoad(workouts = [], options = {}) {
  const { weeks = 8, today = new Date() } = options;
  if (weeks < 1) return [];

  const currentMonday = startOfWeek(today);
  // Use a slightly larger lookback so we can compute the optimal band for the
  // earliest *returned* week from the 4 weeks of priors that precede it.
  const totalSpan = weeks + 4;
  const weekStarts = Array.from({ length: totalSpan }, (_, i) => {
    const d = new Date(currentMonday);
    d.setDate(d.getDate() - (totalSpan - 1 - i) * 7);
    return d;
  });

  // Bucket workouts by week-start key. Keep counts + estimated flag too.
  const buckets = new Map();
  weekStarts.forEach(ws => buckets.set(localDateStr(ws), { tss: 0, count: 0, estimated: false }));

  workouts.forEach(w => {
    if (!w?.date) return;
    const wd = new Date(w.date);
    if (Number.isNaN(wd.getTime())) return;
    const ws = startOfWeek(wd);
    const key = localDateStr(ws);
    const bucket = buckets.get(key);
    if (!bucket) return; // outside the lookback window
    bucket.tss += (w.tss || 0);
    bucket.count += 1;
    if (w.tssEstimated) bucket.estimated = true;
  });

  // Build full series (priors + returned weeks) so each returned week can
  // look back at the previous 4 weeks for its band.
  const series = weekStarts.map(ws => {
    const key = localDateStr(ws);
    const b = buckets.get(key);
    return {
      weekStart: key,
      weekLabel: shortLabel(ws),
      totalTSS: Math.round(b.tss),
      workoutCount: b.count,
      isEstimated: b.count > 0 && b.estimated,
      isCurrentWeek: ws.getTime() === currentMonday.getTime(),
    };
  });

  // Compute optimal band for each week from its 4 prior weeks of *training* data.
  // Skip zero-TSS weeks so an offseason gap doesn't artificially flatten the band.
  for (let i = 0; i < series.length; i++) {
    const priors = series.slice(Math.max(0, i - 4), i).filter(p => p.totalTSS > 0);
    if (priors.length < 2) {
      series[i].optimalLow = null;
      series[i].optimalHigh = null;
      series[i].status = 'unknown';
      continue;
    }
    const avg = priors.reduce((s, p) => s + p.totalTSS, 0) / priors.length;
    const low = Math.round(avg * 0.8);
    const high = Math.round(avg * 1.3);
    series[i].optimalLow = low;
    series[i].optimalHigh = high;
    // Status: 'unknown' if no training this week (band-but-no-data); otherwise
    // compare totalTSS against the band.
    if (series[i].totalTSS === 0) {
      series[i].status = 'under';
    } else if (series[i].totalTSS < low) {
      series[i].status = 'under';
    } else if (series[i].totalTSS > high) {
      series[i].status = 'over';
    } else {
      series[i].status = 'optimal';
    }
  }

  return series.slice(-weeks);
}
